import type {ChangeEvent, ReactElement} from 'react'
import {Icon} from 'pepsico-ds'
import {formatVideoTime} from './formatVideoTime'

interface MinimalVideoControlsProps {
  isPlaying: boolean
  currentTime: number
  duration: number
  bufferedEnd: number
  onTogglePlay: () => void
  onSeek: (seconds: number) => void
}

const toPercent = (value: number, total: number): number =>
  total > 0 ? Math.min(100, Math.max(0, (value / total) * 100)) : 0

/** Play/pause and a seek bar with elapsed and total time. Nothing else on purpose. */
const MinimalVideoControls = ({
  isPlaying,
  currentTime,
  duration,
  bufferedEnd,
  onTogglePlay,
  onSeek,
}: MinimalVideoControlsProps): ReactElement => {
  const played = toPercent(currentTime, duration)
  const buffered = toPercent(bufferedEnd, duration)
  const trackBackground = `linear-gradient(to right, #fff 0%, #fff ${played}%, rgba(255,255,255,0.45) ${played}%, rgba(255,255,255,0.45) ${buffered}%, rgba(255,255,255,0.2) ${buffered}%, rgba(255,255,255,0.2) 100%)`

  return (
    <div data-testid="video-controls" className="flex items-center gap-3 bg-black px-4 py-2 text-white">
      <button
        type="button"
        aria-label={isPlaying ? 'Pause' : 'Play'}
        data-testid="video-play-toggle"
        onClick={onTogglePlay}
        className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-white transition-colors hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-white"
      >
        <Icon icon={isPlaying ? 'pause' : 'play_arrow'} size="medium" style={{color: 'currentColor'}} />
      </button>
      <span className="w-10 shrink-0 text-right text-xs tabular-nums" data-testid="video-current-time">
        {formatVideoTime(currentTime)}
      </span>
      <input
        type="range"
        min={0}
        max={duration > 0 ? duration : 0}
        step={1}
        value={Math.min(currentTime, duration > 0 ? duration : 0)}
        disabled={duration <= 0}
        aria-label="Seek"
        aria-valuetext={`${formatVideoTime(currentTime)} of ${formatVideoTime(duration)}`}
        data-testid="video-seek"
        onChange={(event: ChangeEvent<HTMLInputElement>): void => onSeek(Number(event.target.value))}
        style={{background: trackBackground}}
        className="h-1 min-w-0 flex-1 cursor-pointer appearance-none rounded-full outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
      />
      <span className="w-10 shrink-0 text-xs text-white/80 tabular-nums" data-testid="video-duration">
        {formatVideoTime(duration)}
      </span>
    </div>
  )
}

export default MinimalVideoControls
